import React from 'react';
import { CaretRight, Buildings, Users, Briefcase, ShareNetwork } from '@phosphor-icons/react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@t-one-internal/design-system';
import type { OrgPathItem } from '../../types/api';

interface OrgBreadcrumbProps {
    path: OrgPathItem[] | null | undefined;
    mode?: 'full' | 'truncated' | 'compact';
    maxItems?: number;
    className?: string;
    interactive?: boolean;
    onItemClick?: (item: OrgPathItem, index: number) => void;
}

const LEVEL_ICONS: Record<string, React.ReactNode> = {
    Organization: <Buildings className="w-3.5 h-3.5" />,
    'Business Unit': <Briefcase className="w-3.5 h-3.5" />,
    Division: <ShareNetwork className="w-3.5 h-3.5" />,
    Department: <Users className="w-3.5 h-3.5" />,
    Team: <Users className="w-3.5 h-3.5" />,
};

const OrgBreadcrumb: React.FC<OrgBreadcrumbProps> = ({ path, mode = 'full', maxItems = 2, className = '', onItemClick }) => {
    if (!path || path.length === 0) return null;

    let displayItems = path;
    let isTruncated = false;

    if (mode === 'truncated' && path.length > maxItems) {
        displayItems = path.slice(-maxItems);
        isTruncated = true;
    } else if (mode === 'compact') {
        displayItems = path.slice(-1);
        isTruncated = path.length > 1;
    }

    const renderItem = (item: OrgPathItem, index: number, isLast: boolean) => {
        const Icon = LEVEL_ICONS[item.level] || <Users className="w-3.5 h-3.5" />;
        const content = (
            <span className={`flex items-center gap-1.5 ${isLast ? 'font-medium text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}>
                {!isTruncated && Icon}
                <span className="truncate max-w-[150px]">{item.name}</span>
            </span>
        );

        if (onItemClick) {
            return (
                <button type="button" onClick={(e) => { e.stopPropagation(); e.preventDefault(); onItemClick(item, index); }} className="transition-colors hover:underline cursor-pointer bg-transparent border-0 p-0 text-left">
                    {content}
                </button>
            );
        }
        return content;
    };

    const breadcrumbContent = (
        <div className={`flex items-center text-sm ${className}`}>
            {isTruncated && mode !== 'compact' && (
                <span className="text-slate-400 mr-1.5 flex items-center">
                    <span className="text-xs">...</span>
                    <CaretRight className="w-3 h-3 mx-1 text-slate-400" />
                </span>
            )}
            {displayItems.map((item, index) => {
                const isLast = index === displayItems.length - 1;
                return (
                    <React.Fragment key={item.id}>
                        {renderItem(item, index, isLast)}
                        {!isLast && <CaretRight className="w-3 h-3 mx-1.5 text-slate-400 flex-shrink-0" />}
                    </React.Fragment>
                );
            })}
        </div>
    );

    if (mode !== 'full') {
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div className="cursor-default w-fit max-w-full">{breadcrumbContent}</div>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[350px] bg-slate-800 px-3 py-2">
                        <div className="flex flex-wrap items-center gap-1.5 text-sm">
                            {path.map((p, i) => (
                                <React.Fragment key={p.id}>
                                    <span className="text-white font-medium">{p.name}</span>
                                    {i < path.length - 1 && <CaretRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />}
                                </React.Fragment>
                            ))}
                        </div>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    }

    return breadcrumbContent;
};

export default OrgBreadcrumb;
